import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Badge from "@iscr/views/NUI/Badge";
import { Icon } from "semantic-ui-react";
import { Dot } from "./Dot";
// Syncing is a flag alongside the state rather than one of the states, but it's what the profile is
// actually doing - so it takes over the badge while it's set.
export const SYNC_STATE_DISPLAY = {
    pending: {
        label: "Pending",
        color: "yellow",
        hint: "Details changed - needs finance approval before it can be sent to SAP.",
    },
    approved: {
        label: "Approved",
        color: "blue",
        hint: "Approved - queued to be sent to SAP. Stays here if a previous sync failed; use Sync Approved to SAP to retry.",
    },
    synced: {
        label: "Synced",
        color: "green",
        hint: "SAP has confirmed this profile.",
    },
    quarantined: {
        label: "Quarantined",
        color: "red",
        hint: "Held back from SAP until it's manually released following review.",
    },
};
/** What the badge reads while a hand-off is in flight, whichever state the profile is sitting in. */
const SYNCING_DISPLAY = { label: "Syncing", color: "indigo", dot: "bg-indigo-800" };
/**
 * The one answer to "what does the SAP Status column show for this profile" - `undefined` where it
 * shows nothing at all. A hand-off in flight is what the profile is doing, so it wins the `badge`
 * outright; `state` is what it's sitting under, which is all the hover hint has left to describe.
 *
 * A profile with no state has no badge for a hand-off to take over, so it stays blank even while
 * `syncing` is set - that pairing is reachable, since the row's two sync fields are whatever the
 * freshest record reported. The SAP Status filter matches on this too, so a row can't read one way
 * in the column and answer to a different option in the dropdown.
 */
export const getSapStatusBadge = (state, syncing) => {
    if (!state) {
        return undefined;
    }
    return { badge: syncing ? "syncing" : state, state };
};
export const SyncStateBadge = ({ state, syncing }) => {
    const shown = getSapStatusBadge(state, syncing);
    if (!shown)
        return _jsx("span", { className: "text-gray-400", children: "-" });
    const display = SYNC_STATE_DISPLAY[shown.state];
    // A state the frontend doesn't know about yet (e.g. backend deployed ahead of the UI) is shown
    // raw rather than silently rendered as a dash, so it's obvious something needs updating here.
    if (!display)
        return _jsx("span", { className: "text-gray-500", children: shown.state });
    // The hover hint stays with the underlying state - it's the only thing left that says where the
    // profile will land once a hand-off finishes.
    const badge = shown.badge === "syncing" ? SYNCING_DISPLAY : display;
    // Badge takes no title prop, so the tooltip lives on a wrapper.
    return (_jsx("span", { title: display.hint, children: _jsxs(Badge, { color: badge.color, small: true, className: "whitespace-nowrap inline-flex items-center gap-2", children: [shown.badge === "syncing" && _jsx(Dot, { bgColorClassName: SYNCING_DISPLAY.dot, animating: true }), badge.label] }) }));
};
/**
 * The status badge plus, when the last sync left an error behind, a warning marker. Both live in one
 * cell because the badge alone can't convey it - a profile that failed reads "Approved" (it's queued
 * for retry), identical to one that has simply never been sent. The error is what says which.
 */
export const SyncStateCell = ({ row }) => (_jsxs("span", { className: "flex items-center gap-1", children: [_jsx(SyncStateBadge, { state: row.sap_sync_state, syncing: row.sap_syncing }), row.sap_last_sync_error && (_jsx(Icon, { name: "exclamation triangle", className: "!m-0 leading-none text-red-600", title: `Last sync to SAP failed: ${row.sap_last_sync_error}` }))] }));
